/**
 * Tracks the measured box of the page content element — the
 * `<div id="content" data-testid="page-content-only">` rendered by `ViewPageContent` in
 * `@confluence/view-page-common`.
 *
 * The element registers itself through `setPageContentElement`, which is attached as a callback
 * ref. A callback ref already fires on mount, unmount and replacement, so subscribers never have
 * to search the DOM for the element or keep a `MutationObserver` armed on `document.body` to
 * notice when it changes.
 *
 * State lives at module level so that every subscriber shares a single `ResizeObserver` and a
 * single window `resize` listener, both of which are torn down once the last subscriber leaves.
 */

export type PageContentRect = {
	width: number;
	left: number;
	right: number;
};

/**
 * Shared identity for "not measured yet" so that seeding a subscriber with the current value
 * cannot trigger a re-render before anything has actually been measured.
 */
export const EMPTY_PAGE_CONTENT_RECT: PageContentRect = Object.freeze({
	width: 0,
	left: 0,
	right: 0,
});

export const isSamePageContentRect = (a: PageContentRect, b: PageContentRect): boolean =>
	a.width === b.width && a.left === b.left && a.right === b.right;

type Listener = (rect: PageContentRect) => void;

let element: Element | null = null;
/**
 * Last measurement. Deliberately retained when the element unregisters so consumers keep the last
 * known layout rather than collapsing back to zero, which is how the DOM-lookup implementation
 * this replaces behaved when the element was removed from the page.
 */
let rect: PageContentRect = EMPTY_PAGE_CONTENT_RECT;
const listeners = new Set<Listener>();
let resizeObserver: ResizeObserver | null = null;
let pendingFrame: number | null = null;

/**
 * `left` and `right` are viewport-relative and nothing here listens to scroll. Vertical scrolling
 * leaves both untouched, so the common case is safe; a document-level *horizontal* scroll does move
 * the element without resizing it, and the values then stay stale until the next resize. That is a
 * deliberate carry-over — the DOM-lookup implementation this replaces had no scroll listener
 * either, so both cohorts drift identically and the experiment is not measuring this difference.
 * If drift is ever reported (the sticky draft bar sitting off-column after scrolling sideways),
 * the fix is a passive `scroll` listener calling `scheduleMeasure`.
 */
const measure = () => {
	if (!element) {
		return;
	}

	const { width, left, right } = element.getBoundingClientRect();
	const next = { width, left, right };
	if (isSamePageContentRect(rect, next)) {
		return;
	}

	rect = next;
	listeners.forEach((listener) => listener(rect));
};

const measureOnNextFrame = () => {
	pendingFrame = null;
	measure();
};

/**
 * Used for window resizes only. A resize gesture fires many events per frame and reading layout
 * from an event handler forces a synchronous reflow, so those reads are coalesced to one per
 * frame. `ResizeObserver` callbacks need no coalescing — the browser already delivers at most one
 * per frame, after layout, which makes the read free.
 */
const scheduleMeasure = () => {
	if (pendingFrame !== null) {
		return;
	}
	pendingFrame = requestAnimationFrame(measureOnNextFrame);
};

const stopObserving = () => {
	if (resizeObserver) {
		resizeObserver.disconnect();
		resizeObserver = null;
		window.removeEventListener('resize', scheduleMeasure);
	}

	if (pendingFrame !== null) {
		cancelAnimationFrame(pendingFrame);
		pendingFrame = null;
	}
};

const syncObservers = () => {
	// Read into a local so the null check narrows `el` inside the branch below.
	const el = element;
	const shouldObserve = el !== null && listeners.size > 0;

	if (shouldObserve && !resizeObserver) {
		resizeObserver = new ResizeObserver(measure);
		// Observing delivers an initial callback with the current size, which is what produces the
		// first measurement — no explicit layout read is needed here.
		resizeObserver.observe(el);
		window.addEventListener('resize', scheduleMeasure);
	} else if (!shouldObserve) {
		stopObserving();
	}
};

/**
 * Callback ref for the page content element. Pass the module function itself rather than an inline
 * arrow so that React does not detach and reattach the ref on every render.
 */
export const setPageContentElement = (nextElement: Element | null): void => {
	if (nextElement === element) {
		return;
	}

	element = nextElement;
	// Drop the observer bound to the previous element before re-arming for the new one.
	stopObserving();
	syncObservers();
};

export const getPageContentRect = (): PageContentRect => rect;

export const subscribeToPageContentRect = (listener: Listener): (() => void) => {
	listeners.add(listener);
	syncObservers();

	return () => {
		listeners.delete(listener);
		syncObservers();
	};
};

/**
 * Module state outlives an individual test. Call this between tests that exercise the store.
 */
export const resetPageContentDimensionsStoreForTesting = (): void => {
	listeners.clear();
	element = null;
	stopObserving();
	rect = EMPTY_PAGE_CONTENT_RECT;
};
